import React, { useEffect, useState } from 'react';
import { CreateJobDto } from '../../../../../services/applicant_tracking/JobService/CreateJobDto';
import { GlobalState, useStateMachine } from 'little-state-machine';
import { JobDetails } from '../../../../../services/applicant_tracking/JobService/EditJobDto';
import { JobAndJobPost, initCreateJobDto } from '..';
import { getReferrer } from '../referrers';
import { useForm } from 'react-hook-form';
import { handleFormErrors } from '../../../../../services/ApiService/handler';
import { JobService } from '../../../../../services/applicant_tracking/JobService';
import { Card } from '../../../../../components/Card';
import { JobOrganicPostsLayout } from './JobOrganicPostsLayout';
import { Col, Row } from 'reactstrap';
import {
  JobOrganicPostsDetails,
  prefillCampaignForm,
} from './JobOrganicPostsDetails';
import { JobPostingDetailsForm } from '../../../../JobPage/JobBoardsTab/JobBoardsTabUI/JobPostingDetails';
import { initialize, windowHapi } from '../../../../../config/vonq';
import { redirectWithAlert } from '../../../../../services/FlashService';
import { TokenService } from '../../../../../services/applicant_tracking/analytics/jobboards/TokenService';
import { LocationService } from '../../../../../services/applicant_tracking/LocationService';
import { Location } from '../../../../../entities/applicant_tracking/Location';
import { WindowWithHapi } from '../../../../JobPage/JobBoardsTab';
import { TargetGroupValues } from '../../../../JobPage/JobBoardsTab/JobBoardsTabUI/JobPostingDetails/TargetGroup/TargetGroupValues';
import { PostDetailsService } from '../../../../../services/applicant_tracking/analytics/jobboards/PostDetailsService';
import { PostDetails } from '../../../../../entities/applicant_tracking/jobboards/PostDetails';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { OrganizationService } from '../../../../../services/v1/OrganizationService';
import {
  validateOrganicForm,
  isOrganicPostFormValid,
} from '../../../../JobPage/JobBoardsTab/utils/validators/organicPostFormValidator';
import { JobBoards } from './JobBoards';
import { isIndeedFormValid } from '../../../../JobPage/JobBoardsTab/utils/validators/indeedFormValidator';

interface PropTypes {
  setupStep: number;
  initJobDto: CreateJobDto;
  isEditFlow: boolean;
  jobDetails: JobDetails;
  organicPostSkillsEnabled: boolean;
  onSubmit: (state: CreateJobDto) => Promise<JobAndJobPost>;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  updateLocalStorage: (state: GlobalState, payload: any) => GlobalState;
  atsIndeedIntegrationEnabled: boolean;
}

async function loadTargetGroup(
  window: WindowWithHapi,
): Promise<TargetGroupValues> {
  const [industries, jobFunctions] = await Promise.all([
    window.hapi.product.api.getIndustries(),
    window.hapi.product.api.getJobFunctions(),
  ]);

  return {
    industries,
    jobFunctions,
    educationLevels: [], // Not needed for organic purposes
    seniorities: [], // Not needed for organic purposes
  };
}

export function SetupJobOrganicPostsPage(props: PropTypes) {
  const updateLocalStorage = props.updateLocalStorage;
  const { actions, state } = useStateMachine({ updateLocalStorage });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState<JobPostingDetailsForm>(
    props.initJobDto.organicPostForm,
  );
  const [postDetails, setPostDetails] = useState<PostDetails>(null);
  const [targetGroup, setTargetGroup] = useState(null);

  const { handleSubmit, formState, getValues, setError } =
    useForm<CreateJobDto>({
      mode: 'onChange',
      defaultValues: props.initJobDto,
    });

  const submitData = async (data: CreateJobDto) => {
    setIsSaving(true);
    try {
      await props.onSubmit({ ...data, organicPostForm: form });

      return true;
    } catch (e: unknown) {
      handleFormErrors(e, setError);
      setIsSaving(false);
    }

    return false;
  };

  const jobPublished = props.jobDetails?.status === 'published';

  const onSubmit = handleSubmit(async (data: CreateJobDto) => {
    const isSuccessful = await submitData(data);
    if (!isSuccessful) return;

    const jobName = state.setupJob.name;
    await JobService.publish(state.setupJob.jobId.toString(), true);
    actions.updateLocalStorage(initCreateJobDto);

    if (jobPublished) {
      window.location.href = getReferrer();
    } else {
      redirectWithAlert(
        '/jobs?status=published',
        `'**${jobName}**' was published successfully.`,
      );
    }
  });

  const onSave = handleSubmit(async (data: CreateJobDto) => {
    const isSuccessful = await submitData(data);
    if (!isSuccessful) return;

    const jobName = state.setupJob.name;
    await JobService.submit(state.setupJob.jobId.toString());
    actions.updateLocalStorage(initCreateJobDto);
    redirectWithAlert(
      '/jobs?status=open',
      `'**${jobName}**' was saved as open successfully.`,
    );
  });

  const onSaveAsDraft = handleSubmit(async (data: CreateJobDto) => {
    const isSuccessful = await submitData(data);
    if (!isSuccessful) return;

    const jobName = state.setupJob.name;
    actions.updateLocalStorage(initCreateJobDto);
    redirectWithAlert(
      '/jobs?status=draft',
      `'**${jobName}**' was saved as draft successfully.`,
    );
  });

  const onNavigate = () => {
    actions.updateLocalStorage({ ...getValues(), organicPostForm: form });
  };

  const window = windowHapi();

  function handleInputChange(name: string, value: unknown) {
    const newForm = { ...form, [name]: value };
    setForm(newForm);

    setFormErrors({ ...formErrors, ...validateOrganicForm(newForm) });
  }

  async function injectVonqElements() {
    const token = await TokenService.get();
    const organization = await OrganizationService.current({
      expand: ['organization_info'],
    });

    try {
      if (token) {
        await initialize(token.token, organization.organizationInfo?.currency);
        setIsLoading(false);
      } else {
        // handle the error
      }
    } catch (e: any) {
      // handle the error
    }
  }

  useEffect(() => {
    (async () => {
      const callback = async () => {
        await injectVonqElements();
        removeEventListener('hapi:load:injector', callback);
      };

      if (window.hapiInjector == null) {
        window.addEventListener('hapi:load:injector', callback);
      } else {
        await injectVonqElements();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isLoading) {
        if (form == null) {
          let jobPostData: PostDetails = null;
          let location: Location = null;

          if (state.setupJob?.jobId || state.setupJob?.referenceJobId) {
            const postDetailList = await PostDetailsService.list({
              jobId: state.setupJob?.referenceJobId || state.setupJob?.jobId,
              expand: ['organic_post_skills'],
            });
            jobPostData = postDetailList.postDetails?.[0];
            setPostDetails(jobPostData);
          }

          if (!props.isEditFlow || props.jobDetails.status === 'draft') {
            // Prefill LinkedIn with the previous Job's value.
            const postDetailList = await PostDetailsService.list({
              order: 'created_at desc',
              limit: 1,
              withCompanyId: true,
            });
            const prevPostDetail = postDetailList.postDetails?.[0];
            if (jobPostData) {
              jobPostData.companyId = prevPostDetail?.companyId;
            } else {
              jobPostData = { id: null, companyId: prevPostDetail?.companyId };
            }
          }

          try {
            if (state.setupJob?.locationId != null) {
              location = await LocationService.get(state.setupJob.locationId);
            }
          } catch (e: any) {
            // ignore
          }

          setForm(
            prefillCampaignForm(
              state.setupJob,
              location,
              jobPostData,
              props.atsIndeedIntegrationEnabled,
            ),
          );
        }

        setTargetGroup(await loadTargetGroup(window));
      }
    })();
  }, [isLoading]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card>
      <form>
        <JobOrganicPostsLayout
          setupStep={props.setupStep}
          setSetupStep={props.setSetupStep}
          isEditFlow={props.isEditFlow}
          isLoading={isLoading}
          isSaving={isSaving}
          organicEnabled={form?.organicEnabled}
          indeedEnabled={form?.indeedEnabled}
          organicPostValid={
            form?.organicEnabled ? isOrganicPostFormValid(form) : true
          }
          indeedFormValid={form?.indeedEnabled ? isIndeedFormValid(form) : true}
          formState={formState}
          onCancel={() => {
            actions.updateLocalStorage(initCreateJobDto);
            window.location.href = getReferrer();
          }}
          onSave={onSave}
          onSaveAsDraft={onSaveAsDraft}
          onSubmit={onSubmit}
          jobDetails={props.jobDetails}
          onNavigate={onNavigate}
        >
          <div className='d-flex flex-column gap-2h mt-4'>
            <Row>
              <Col xs='8'>
                <JobBoards
                  organicEnabled={form?.organicEnabled}
                  indeedEnabled={form?.indeedEnabled}
                  indeedEasyApplyEnabled={form?.indeedEasyApplyEnabled}
                  form={form}
                  atsIndeedIntegrationEnabled={
                    props.atsIndeedIntegrationEnabled
                  }
                  handleInputChange={handleInputChange}
                />
              </Col>
              <Col xs='4'>
                <JobOrganicPostsDetails
                  job={state.setupJob}
                  isLoading={isLoading}
                  form={form}
                  postDetails={postDetails}
                  targetGroup={targetGroup}
                  organicPostSkillsEnabled={props.organicPostSkillsEnabled}
                  onInputChange={handleInputChange}
                  setForm={setForm}
                  formErrors={formErrors}
                  disabled={!(form?.organicEnabled || form?.indeedEnabled)}
                />
              </Col>
            </Row>
          </div>
        </JobOrganicPostsLayout>
      </form>
    </Card>
  );
}
